import { Backdrop, Button, CircularProgress, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Trash01 } from 'untitledui-js-base';
import { useCommonApi } from '../api';
import { onSuccessCustomApiResponse } from '../api/utils';
import { useNotify } from '../hooks';
import { PATHS } from '../paths';
import { AccidentCreatingStepAccidentInformations } from './accidents/AccidenCreatingStepAccidentInformations';
import { AccidentCreatingStepCarInformations } from './accidents/AccidenCreatingStepCarInformations';
import {
  AccidentCreatingStepCustomerInformations,
  SameAsValue
} from './accidents/AccidenCreatingStepCustomerInformations';
import { AccidentCreatingStepPhoto } from './accidents/AccidentCreatingStepPhoto';
import { AccidentCreatingStepRecap } from './accidents/AccidentCreatingStepRecap';
import { AccidentRemoveConfirmDialog } from './accidents/AccidentRemoveConfirmDialog';
import { parseAccidentDataToRequest } from './accidents/parser';
import { initializeSameAsValues } from './accidents/utils/sameAsCheckers';
import { PageLayout } from './common';
import { useLoadAresData } from './hooks/useLoadAresData';
import { useRemoveAccidentFile } from './hooks/useRemoveAccidentFile';
import { useUploadFiles } from './hooks/useUploadFiles';
import {
  ACCIDENT_FILES,
  Accident,
  AccidentExistingUserResponse,
  AccidentInsuranceList,
  AccidentUserType,
  CarModelsList,
  PhotoNoteType,
  RegistryIdName,
  RegistryNameLabel,
  RegistryType
} from './types/accident';

import 'yet-another-react-lightbox/styles.css';
import { debounce } from 'lodash';
import { useGetAutocompleteAddress } from '../hooks/useGetAutocompleteAddress';
import { useAccount } from './accidents/hooks/useAccount';
import { cleanAccidentData } from './accidents/utils/cleanAccidentData';
import { SignatureDialog } from './common/SignatureDialog';

const steps = ['Údaje o vozidle', 'Údaje o nehodě', 'Údaje o řidiči', 'Fotografie', 'Rekapitulace'];

interface AccidentCreationPageContentProps {
  id?: string;
  accident?: Accident;
}

const AccidentCreationPageContent = ({ id, accident }: AccidentCreationPageContentProps) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean;
  }>({});
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const queryClient = useQueryClient();
  const { notifyOnSuccess, notifyOnError } = useNotify();

  const [contactSameAsOwner, setContactSameAsOwner] = useState<SameAsValue>('user');
  const [driverSameAsOwner, setDriverSameAsOwner] = useState<SameAsValue>('user');
  const [operatorSameAsOwner, setOperatorSameAsOwner] = useState(true);
  const [userType, setUserType] = useState<AccidentUserType>(AccidentUserType.Person);
  const [operatorType, setOperatorType] = useState<AccidentUserType>(AccidentUserType.Person);
  const [selectedDocumentName, setSelectedDocumentName] = useState<ACCIDENT_FILES | null>(null);

  const formCarRef = useRef<HTMLFormElement | null>(null);
  const formAccidentRef = useRef<HTMLFormElement | null>(null);
  const formCustomerRef = useRef<HTMLFormElement | null>(null);

  const { isAresDataLoading, aresData, handleLoadAresData } = useLoadAresData();
  const {
    isAresDataLoading: isOperatorAresDataLoading,
    aresData: operatorAresData,
    handleLoadAresData: handleLoadOperatorAresData
  } = useLoadAresData();
  const [existingUserData, setExistingUserData] = useState<AccidentExistingUserResponse | undefined>(undefined);
  const [existingOperatorData, setExistingOperatorData] = useState<AccidentExistingUserResponse | undefined>(undefined);
  const { handleRemoveFile, isRemoving } = useRemoveAccidentFile({
    accidentId: accident?.id as number
  });
  const { handleSetImage, isUploadingImages } = useUploadFiles(accident as Accident);
  const { handleOnSearch, autocompleteItems, isLoadingData } = useGetAutocompleteAddress();
  const {
    handleOnSearch: handleOnSearchOperator,
    autocompleteItems: operatorAutocompleteItems,
    isLoadingData: isLoadingOperatorData
  } = useGetAutocompleteAddress();
  const {
    handleGetAccountEmail,
    accountResult,
    setAddNewAccountDialogOpen,
    addNewAccountDialogOpen,
    isCreatingAccount,
    handleCreateAccount
  } = useAccount();

  const { data: carBrandsAndModels, isLoading: loadingCarBrandsAndModels } = useQuery<CarModelsList[]>(
    useCommonApi().getRegistryList(RegistryType.CAR_MODELS)
  );
  const { data: insurances } = useQuery<AccidentInsuranceList[]>(
    useCommonApi().getRegistryList(RegistryType.INSURANCES)
  );
  const { data: insuranceTypes, isLoading: insuranceTypesLoading } = useQuery<RegistryNameLabel[]>(
    useCommonApi().getRegistryList(RegistryType.INSURANCE_TYPES)
  );
  const { data: countryList } = useQuery<RegistryIdName[]>(useCommonApi().getRegistryList(RegistryType.COUNTRY_LIST));

  const { mutate: updateAccidentData, isPending: isUpdating } = useMutation(useCommonApi().updateAccident());
  const { mutate: deleteAccident } = useMutation(useCommonApi().removeAccident());

  const { mutateAsync: addInsuranceAsync, isPending: isAdditingInsurance } = useMutation(
    useCommonApi().addInsuranceCompany()
  );
  const { mutate: signAccidentDocument, isPending: isGeneratingDocument } = useMutation(
    useCommonApi().signAccidentDocument()
  );
  const { mutate: getExistingUser } = useMutation(useCommonApi().getExistingUser());

  // Inicializace "sameAs" hodnot z accident dat (při načtení nebo refresh)
  React.useEffect(() => {
    if (accident) {
      const sameAsValues = initializeSameAsValues(accident);
      setOperatorSameAsOwner(sameAsValues.operatorSameAsOwner);
      setDriverSameAsOwner(sameAsValues.driverSameAsOwner);
      setContactSameAsOwner(sameAsValues.contactSameAsOwner);

      // Inicializace typů uživatelů
      if (accident.user?.type?.name) {
        setUserType(accident.user.type.name as AccidentUserType);
      }
      if (accident.operator?.type?.name && !sameAsValues.operatorSameAsOwner) {
        setOperatorType(accident.operator.type.name as AccidentUserType);
      }
    }
  }, [accident]);

  const handleGetExistingUser = (identification: string, isOperator?: boolean) => {
    const targetType = isOperator
      ? operatorType === AccidentUserType.Company || operatorType === AccidentUserType.BusinessMan
        ? 'company'
        : 'person'
      : userType === AccidentUserType.Company || userType === AccidentUserType.BusinessMan
        ? 'company'
        : 'person';

    getExistingUser(
      {
        identification,
        type: targetType
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: (_, data) => {
            if (isOperator) {
              setExistingOperatorData(data);
            } else {
              setExistingUserData(data);
            }
          }
        })
      }
    );
  };

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted() ? steps.findIndex((step, i) => !(i in completed)) : activeStep + 1;

    // if there is first step, update accident and continue
    if (activeStep === 0 && formCarRef.current) {
      formCarRef.current.requestSubmit();
      return;
    }

    if (activeStep === 1 && formAccidentRef.current) {
      formAccidentRef.current.requestSubmit();
      return;
    }

    if (activeStep === 2 && formCustomerRef.current) {
      formCustomerRef.current.requestSubmit();
      return;
    }

    setActiveStep(newActiveStep);
    setCompleted({ ...completed, [activeStep]: true });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleAresClick = (vatNumber: string, isOperator?: boolean) => {
    if (isOperator) {
      handleLoadOperatorAresData(vatNumber);
    } else {
      handleLoadAresData(vatNumber);
    }
  };

  const handleAutocompleteSearch = (value: string, isOperator?: boolean) => {
    if (isOperator) {
      handleOnSearchOperator(value);
    } else {
      handleOnSearch(value);
    }
  };

  const handleAccidentUpdate = (data: Accident) => {
    // Vyčistit objekty, které mají equalTo - ponechat jen equalTo
    const cleanedData = cleanAccidentData(data);

    const updatedData = {
      ...(accident ?? {}),
      ...cleanedData
    };

    updateAccidentData(
      {
        id: id as string,
        accidentData: parseAccidentDataToRequest(updatedData)
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            queryClient.invalidateQueries({
              queryKey: ['common', 'accidents', 'detail', id]
            });
            setActiveStep(activeStep + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        })
      }
    );
  };

  const handleAccidentRemove = () => {
    deleteAccident(
      { accidentId: id as string },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            navigate(PATHS.HOME);
            notifyOnSuccess('Nehoda byla smazána!');
          },
          failed: () => {
            setOpenRemoveDialog(false);
            notifyOnError('Nelze smazat nehodu!');
          }
        })
      }
    );
  };

  const debouncedUpdatePhotoNote = debounce((data: PhotoNoteType) => {
    updateAccidentData({
      id: accident?.id.toString() ?? '',
      accidentData: {
        mediaGroups: {
          [data.photoName]: data.note
        }
      }
    });
  }, 500);

  const handleUpdatePhotoNote = (data: PhotoNoteType) => {
    debouncedUpdatePhotoNote(data);
  };

  const handleInsuranceAdd = async (title: string): Promise<{ id: number; title: string } | undefined> => {
    try {
      const data = await addInsuranceAsync({ title });
      return {
        id: Number.parseInt(data.results.id),
        title
      };
    } catch (error) {
      console.error('Chyba při přidávání pojišťovny:', error);
      return undefined;
    }
  };

  const handleSignDocument = (signatureBase64: string) => {
    if (!selectedDocumentName) return null;
    signAccidentDocument(
      {
        document: selectedDocumentName,
        accidentId: Number.parseInt(id as string),
        files: [
          {
            filename: 'sign.png',
            data: signatureBase64,
            template: 'tenantSignature'
          }
        ]
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            notifyOnSuccess('Document byl podepsán!');
            setSelectedDocumentName(null);
          },
          failed: () => {
            notifyOnError('Chyba při podepisování dokumentu!');
          }
        })
      }
    );
  };

  const handleGenerateDocument = (documentName: string) => {
    if (!documentName) return null;
    signAccidentDocument(
      {
        document: documentName,
        accidentId: accident?.id as number
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            notifyOnSuccess('Dokument byl vygenerovaný!');
          },
          failed: () => {
            notifyOnError('Chyba při generování dokumentu!');
          }
        })
      }
    );
  };

  const RenderButton = () => {
    switch (activeStep) {
      case 4:
        return (
          <Button
            variant="contained"
            size="large"
            color="success"
            onClick={() => navigate(PATHS.ACCIDENT_CONFIRM(accident?.id.toString() as string, true))}
            endIcon={<ChevronRight size="12px" />}
            fullWidth
          >
            Založit nehodu
          </Button>
        );
      default:
        return (
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleNext}
            endIcon={<ChevronRight />}
            loading={isUpdating}
          >
            Pokračovat
          </Button>
        );
    }
  };

  return (
    <PageLayout
      title="Založení nehody"
      extra={
        <Button variant="outlined" color="primary" startIcon={<Trash01 />} onClick={() => setOpenRemoveDialog(true)}>
          Odstranit nehodu
        </Button>
      }
    >
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'background.paper',
          paddingX: 3,
          paddingY: 3
        }}
      >
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label} completed={completed[index]}>
              <StepButton color="inherit" onClick={handleStep(index)} disabled>
                <Box component="span" display={{ xs: 'none', lg: 'inline-block' }}>
                  {label}
                </Box>
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>

      {activeStep === 0 &&
        (loadingCarBrandsAndModels || insuranceTypesLoading ? (
          <Stack direction="row" justifyContent="center" mt={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <AccidentCreatingStepCarInformations
            onSubmit={handleAccidentUpdate}
            formRef={formCarRef}
            accident={accident}
            carBrandsAndModels={carBrandsAndModels}
          />
        ))}
      {activeStep === 1 && (
        <AccidentCreatingStepAccidentInformations
          onSubmit={handleAccidentUpdate}
          formRef={formAccidentRef}
          accident={accident}
          insurances={insurances}
          insuranceTypes={insuranceTypes}
          isAdditingInsurance={isAdditingInsurance}
          onInsuranceAdd={handleInsuranceAdd}
        />
      )}
      {activeStep === 2 && (
        <AccidentCreatingStepCustomerInformations
          onSubmit={handleAccidentUpdate}
          formRef={formCustomerRef}
          accident={accident}
          contactSameAsOwner={contactSameAsOwner}
          driverSameAsOwner={driverSameAsOwner}
          operatorSameAsOwner={operatorSameAsOwner}
          onChangeContactSameAsOwner={(value: SameAsValue) => setContactSameAsOwner(value)}
          onChangeDriverSameAsOwner={(value: SameAsValue) => setDriverSameAsOwner(value)}
          onChangeOperatorSameAsOwner={(value: boolean) => {
            setOperatorSameAsOwner(value);
            if (!value) {
              setOperatorType(AccidentUserType.Person);
            }
          }}
          onAresClick={handleAresClick}
          aresData={aresData}
          operatorAresData={operatorAresData}
          isAresDataLoading={isAresDataLoading || isOperatorAresDataLoading}
          userType={userType}
          handleSetUserType={setUserType}
          operatorType={operatorType}
          handleSetOperatorType={setOperatorType}
          onAutocompleteSearch={handleAutocompleteSearch}
          autocompleteResults={autocompleteItems}
          operatorAutocompleteResults={operatorAutocompleteItems}
          isLoadingAddressData={isLoadingData || isLoadingOperatorData}
          countryList={countryList}
          existingUserData={existingUserData}
          existingOperatorData={existingOperatorData}
          onGetExistingDataClick={handleGetExistingUser}
          addNewAccountDialogOpen={addNewAccountDialogOpen}
          handleGetAccountEmail={handleGetAccountEmail}
          handleCreateAccount={handleCreateAccount}
          isCreatingAccount={isCreatingAccount}
          accountResult={accountResult}
          setAddNewAccountDialogOpen={setAddNewAccountDialogOpen}
        />
      )}
      {activeStep === 3 && (
        <AccidentCreatingStepPhoto
          images={accident?.media?.images}
          onUpload={(category, images) => handleSetImage(category, images, true)}
          onRemoveFile={handleRemoveFile}
          isUploading={isUploadingImages || isRemoving}
          onNoteSave={handleUpdatePhotoNote}
        />
      )}
      {activeStep === 4 && (
        <AccidentCreatingStepRecap
          accident={accident as Accident}
          setSelectedDocumentName={(template) => setSelectedDocumentName(template)}
          handleGenerateDocument={(template) => handleGenerateDocument(template)}
          accidentDocuments={accident?.documents}
          isGeneratingDocument={isGeneratingDocument}
        />
      )}
      <Stack direction="row" justifyContent="center" gap={2} mt={4}>
        {activeStep > 0 && (
          <Button variant="text" size="large" onClick={handleBack} startIcon={<ChevronLeft />}>
            Zpět
          </Button>
        )}

        <RenderButton />
      </Stack>
      <Backdrop
        sx={(theme) => ({
          color: '#fff',
          zIndex: theme.zIndex.drawer + 1
        })}
        open={(isUploadingImages || isRemoving) ?? false}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <AccidentRemoveConfirmDialog
        open={openRemoveDialog}
        onClose={() => setOpenRemoveDialog(false)}
        onDeny={() => setOpenRemoveDialog(false)}
        onAccept={handleAccidentRemove}
      />
      <SignatureDialog
        open={!!selectedDocumentName}
        onClose={() => {
          setSelectedDocumentName(null);
        }}
        onSave={async (signatureBase64) => handleSignDocument(signatureBase64)}
        loading={isGeneratingDocument}
      />
    </PageLayout>
  );
};

export const AccidentCreationPage = () => {
  const { accidentId } = useParams();
  const { data: accidentData } = useQuery(useCommonApi().getAccident(accidentId as string));

  return <AccidentCreationPageContent id={accidentId} accident={accidentData} />;
};
